import React, { Fragment, cloneElement, isValidElement } from 'react';
import { Box, Grid, Card, CardContent, Typography, Button, Skeleton, Tooltip } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import FilterBar from './FilterBar';
import EnhancedTable from './EnhancedTable';
import CurrencyDisplay from './CurrencyDisplay';
import PermissionGate from './PermissionGate';
import PrintPreview from './PrintPreview';
import ReportHeader from './ReportHeader';
import TableSkeleton from './TableSkeleton';
import ReportPrintDialog from './ReportPrintDialog';
import CurrencyInput from './CurrencyInput';
import CompanySwitcher from './CompanySwitcher';
import AuditTimeline from './AuditTimeline';
import EmptyState from './EmptyState';
import ActionMenu from './ActionMenu';
import StatusBadge from './StatusBadge';

// Re-export imported components as named exports
export {
  FilterBar,
  EnhancedTable,
  CurrencyDisplay,
  PermissionGate,
  PrintPreview,
  ReportHeader,
  TableSkeleton,
  ReportPrintDialog,
  CurrencyInput,
  CompanySwitcher,
  AuditTimeline,
  EmptyState,
  ActionMenu,
  StatusBadge
};

// Standard Action Bar - used at top of every page
export const ActionBar = ({ left = null, right = null, sx = {} }) => (
  <Box sx={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    mb: 3,
    ...sx
  }}>
    <Box sx={{ display: 'flex', gap: 1 }}>{left}</Box>
    <Box sx={{ display: 'flex', gap: 1 }}>{right}</Box>
  </Box>
);

// Summary Cards Skeleton
export const SummaryCardsSkeleton = ({ count = 4, gridProps = { xs: 12, sm: 6, md: 3 } }) => (
  <Grid container spacing={2.5} sx={{ mb: 3 }}>
    {[...Array(count)].map((_, idx) => (
      <Grid item {...gridProps} key={idx}>
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ p: 2.5 }}>
            <Skeleton variant="text" width="60%" height={20} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="80%" height={40} />
            <Skeleton variant="text" width="40%" height={16} sx={{ mt: 1 }} />
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

// Standard Summary Cards - consistent metrics display
export const SummaryCards = ({ 
  items,
  cards, 
  loading = false, 
  gridProps = { xs: 12, sm: 6, md: 3 }, 
  threshold = 10000000,
  sx = {} 
}) => {
  const cardList = Array.isArray(items) ? items : (Array.isArray(cards) ? cards : []);
  if (loading) return <SummaryCardsSkeleton count={cardList.length || 4} gridProps={gridProps} />;
  if (!cardList || !cardList.length) return null;

  return (
    <Grid container spacing={2.5} sx={{ mb: 3, ...sx }}>
      {cardList.map((item, idx) => (
        <Grid item {...gridProps} key={idx}>
          <Card 
            sx={{ 
              height: '100%',
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              boxShadow: 'none',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: (theme) => theme.palette.mode === 'light' 
                  ? '0 8px 16px rgba(37, 51, 0, 0.08)' 
                  : '0 8px 16px rgba(0, 0, 0, 0.4)',
                borderColor: 'primary.main'
              }
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: item.icon ? 2 : 1 }}>
                {item.icon && (
                  <Box sx={{ 
                    color: item.color || 'primary.main', 
                    marginInlineEnd: 2, 
                    display: 'flex' 
                  }}>
                    {item.icon}
                  </Box>
                )}
                <Typography color="text.secondary" variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                  {item.label || item.title}
                </Typography>
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  color: 'text.primary',
                  mb: 0.5,
                  fontFamily: (theme) => theme.direction === 'rtl' ? theme.typography.fontFamily : '"Outfit", "Inter", sans-serif'
                }}
              >
                {React.isValidElement(item.value) ? (
                  item.value
                ) : item.isCurrency ? (
                  <CurrencyDisplay amount={item.value} showSymbol={true} compact={true} threshold={threshold} />
                ) : (
                  typeof item.value === 'number' ? item.value.toLocaleString() : (item.value ?? 0)
                )}
              </Typography>
              {(item.subtitle || item.trend !== undefined) && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  {item.trend !== undefined && item.trend !== null && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: parseFloat(item.trend) >= 0 ? 'success.main' : 'error.main',
                        fontWeight: 600,
                        marginInlineEnd: 1,
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '0.75rem'
                      }}
                    >
                      {parseFloat(item.trend) >= 0 ? '+' : ''}{item.trend}%
                    </Typography>
                  )}
                  {item.subtitle && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      {item.subtitle}
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

// Wraps an action element: if isAllCompanies and element looks like an "Add" button, disables it
const wrapActionForAllCompanies = (actionItem, index) => {
  if (!isValidElement(actionItem)) return <Fragment key={index}>{actionItem}</Fragment>;

  const props = actionItem.props || {};
  const label = typeof props.children === 'string' ? props.children.toLowerCase() : '';
  const isAddButton = label.includes('add') || label.includes('new') || label.includes('create');

  if (isAddButton) {
    return (
      <Tooltip key={index} title="Select a specific company to add records">
        <span>{cloneElement(actionItem, { disabled: true })}</span>
      </Tooltip>
    );
  }
  return <Fragment key={index}>{actionItem}</Fragment>;
};

// Standard Page Header - title + action buttons
export const PageHeader = ({ title, subtitle, action, actions, onPrint, isAllCompanies = false, children }) => (
  <Box sx={{ mb: 4 }}>
    <Box sx={{ 
      display: 'flex', 
      flexDirection: { xs: 'column', sm: 'row' },
      justifyContent: 'space-between', 
      alignItems: { xs: 'stretch', sm: 'flex-start' }, 
      mb: subtitle ? 1 : 0,
      gap: 2
    }}>
      <Box sx={{ flex: 1 }}>
        <Typography variant="h1" component="h1" sx={{ mb: subtitle ? 0.5 : 0 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}{isAllCompanies ? ' — All Companies' : ''}
          </Typography>
        )}
      </Box>
      {(action || actions) && (
        <Box sx={{ 
          display: 'flex', 
          gap: 1.5, 
          flexShrink: 0, 
          flexWrap: 'wrap',
          justifyContent: { xs: 'flex-start', sm: 'flex-end' } 
        }}>
          {action && (isAllCompanies ? wrapActionForAllCompanies(action, 'action') : <Fragment key="action">{action}</Fragment>)}
          {Array.isArray(actions)
            ? actions.map((actionItem, index) =>
                isAllCompanies ? wrapActionForAllCompanies(actionItem, index) : <Fragment key={index}>{actionItem}</Fragment>
              )
            : (isAllCompanies && isValidElement(actions) ? wrapActionForAllCompanies(actions, 0) : actions)
          }
        </Box>
      )}
    </Box>
    {children}
  </Box>
);

// Standard Section Header
export const SectionHeader = ({ title, gutterBottom = true, sx = {} }) => (
  <Typography
    variant="h2"
    sx={{
      fontWeight: 700,
      mb: gutterBottom ? 3 : 0,
      ...sx
    }}
  >
    {title}
  </Typography>
);

// Standard Refresh Button
export const RefreshButton = ({ onClick, disabled = false, loading = false, label = 'Refresh', color = 'secondary', variant = 'contained' }) => (
  <Button
    variant={variant}
    color={color}
    startIcon={<Refresh />}
    onClick={onClick}
    disabled={disabled || loading}
    sx={{
      '& .MuiButton-startIcon': { mr: { xs: 0, md: 1 } },
      minWidth: { xs: 'auto', md: 'auto' },
      px: { xs: 1.5, md: 2.5 },
      fontWeight: 600,
      borderRadius: '8px'
    }}
  >
    <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>{label}</Box>
  </Button>
);

// Standard Table with FilterBar - most common pattern
export const StandardTable = ({
  // Data props
  data = [],
  columns = [],

  // Filter props
  basicFilters = [],
  advancedFilters = [],
  activeFilters = [],
  onClearAllFilters,

  // Table props
  actions,
  emptyMessage,
  showTotals = false,
  footerData,
  defaultSortColumn,
  defaultSortDirection = 'asc',
  showPagination = true,
  defaultPageSize = 50,

  // Other props
  title,
  sx = {},
  ...otherProps
}) => (
  <Box sx={sx}>
    {(basicFilters.length > 0 || advancedFilters.length > 0) && (
      <FilterBar
        basicFilters={basicFilters}
        advancedFilters={advancedFilters}
        activeFilters={activeFilters}
        onClearAllFilters={onClearAllFilters}
        sx={{ mb: 3 }}
      />
    )}

    <EnhancedTable
      title={title}
      data={data}
      columns={columns}
      actions={actions}
      emptyMessage={emptyMessage}
      showTotals={showTotals}
      footerData={footerData}
      defaultSortColumn={defaultSortColumn}
      defaultSortDirection={defaultSortDirection}
      showPagination={showPagination}
      defaultPageSize={defaultPageSize}
      {...otherProps}
    />
  </Box>
);

const StandardComponents = {
  ActionBar,
  SummaryCards,
  SummaryCardsSkeleton,
  PageHeader,
  ReportHeader,
  SectionHeader,
  RefreshButton,
  StandardTable,
  FilterBar,
  EnhancedTable,
  PermissionGate,
  PrintPreview,
  TableSkeleton,
  ReportPrintDialog,
  CurrencyInput,
  CompanySwitcher,
  AuditTimeline,
  EmptyState,
  ActionMenu,
  StatusBadge
};

export default StandardComponents;
